import { type ReactNode, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { PendingPhase } from './PendingPhase';
import { Reconciliation, type StepDetail } from './Reconciliation';
import { AllSubSteps, SUB_TABLE_CARD } from './AllSubSteps';
import { useSubStepTable } from './SubStepTable';
import { AssemblyTree } from './AssemblyTree';
import { CenteredSelect } from './CenteredSelect';
import { usePlace } from './lastPlace';
import { useArrowSteps } from './stepThrough';
import { PartSelector, type SelectorPart } from './PartSelector';
import { ImageLightbox } from './ImageLightbox';
import { useAssemblySteps } from './categoryBands';
import { usePartsRun } from './partsRun';
import { useStepParts } from './stepPartsRun';
import { useStepInstructions } from './stepInstructionsRun';
import { useStepsRun } from './stepsRun';
import { useCategories } from './catalogCategories';
import { useStepSections } from './stepSections';
import { useLanguage } from '../../contexts/LanguageContext';

// Phase 4 — Steps & substeps: WHAT EACH STEP IS MADE OF. One Gemini call per step, four
// in flight, producing its tools, its instructions, which earlier steps it builds on, and
// its breakdown into sub-steps with a per-sub-step allocation reconciled server-side to
// partition the step totals.
//
// The repartition — which parts each step adds — was a second view of this phase, and is
// a phase of its own now (see StepPartsPanel). It is a different question with a different
// call, and this analysis is what somebody comes back to; a phase that answers one thing
// does not need a switch on it.
//
// THE PAGE STANDS ON ONE SUB-STEP, and picks which one off a strip of all of them — the
// parts phase's selector, every place on screen at once. It stepped
// through them with a ‹ 1A › pager for a while, which Phase 5 still uses and which is the
// right control for a walk of six: over forty gestures it is thirty presses to reach 3F and
// no way to see that 3F is where you are going. It showed the whole manual at once before
// that — a matrix of every part against every step and every gesture, several screens wide —
// which is the right shape for "do these numbers add up" and the wrong one for the question
// people actually bring here: what does THIS gesture say it does, read against the diagram it
// was read from.
//
// The reconciliation keeps the first question and keeps asking it over everything: its
// verdict sits above the navigator, unchanged and still computed from the whole manual. What
// it no longer draws here is the table under that verdict — see `verdictOnly`. The full
// matrix is still a click away in Phase 3, which is the phase whose numbers it is about.
//
// The RUN is unchanged and still asks about every step at once: the steps are independent,
// and one button that reads the manual is not the same thing as one page that shows it.
// The overview's key in the walk. Not a `category#letter` and unable to collide with one.
const ALL_SUBS = '\x1fall';
// And the tree's, last: the finished thing with everything under it — or one step's pieces,
// chosen in its own corner (see the dropdown on AssemblyTree). Same shape of key, same reason —
// a character no category has in it.
const TREE_VIEW = '\x1ftree';
// AND ONE STEP'S, per step: the same table cut to the gestures of that step. Same shape of
// key, same reason — a category can be anything the classification wrote, so the marker is a
// character no category has in it.
const STEP_SUBS = (category: string) => `\x1fstep${category}`;

export function MergedStep({
  docId,
  file,
  locked,
  held,
}: {
  docId: string;
  file: Blob;
  locked?: boolean;
  // The run that holds this phase is paused: it is not coming until Reprendre.
  held?: boolean;
}) {
  const { t, language } = useLanguage();
  const { steps } = useAssemblySteps(docId);
  const { items } = usePartsRun(docId);
  // What each step adds and what it achieves, from Phase 2. This phase reads it and never
  // writes it.
  const { byStep, text: stepText, ...alloc } = useStepParts(docId);
  // What each step prints, from Phase 3's transcription — displayed here, beside the
  // sub-steps that were written from it.
  const said = useStepInstructions(docId);
  const st = useStepsRun(docId);

  // Page-section image(s) per step — from the PDF (no AI), shown as soon as Phase 1 has run.
  const sections = useStepSections(file, steps);
  const [zoom, setZoom] = useState<string | null>(null);

  // During a "Run steps" pipeline, this phase is "pending" until it runs: an earlier
  // phase is still executing and no per-step analysis exists yet. An error ends the
  // pending state so the failure shows (and the pipeline stops downstream).
  const busy = st.busy;
  const doneSteps = Object.values(st.subSteps).some((a) => a.length > 0);
  const pending = !!locked && !busy && !st.error && !doneSteps;

  // The reconciliation's verdict, once the parts have been shared out among the steps and
  // there is at least one part with a supplied quantity to check — the same test
  // Reconciliation itself applies, so the block is never drawn empty.
  const hasReconRows = items.some((p) => p.qty != null && !p.tool);
  const showRecon = hasReconRows && Object.keys(byStep).length > 0;

  // Everything this phase knows about each step that is not a quantity, handed to the
  // table so a step can be read in one place. It used to be a card under the table, which
  // meant reading a step meant reading two things and holding one against the other.
  // PHASE 3'S ANSWERS FIRST, THIS PHASE'S OLD ONES AS A FALLBACK. The step's tools and its
  // printed text are Phase 3's now — see stepPartsRun and stepInstructionsRun, which argue
  // the move — and this phase goes on DISPLAYING them beside the sub-steps written from
  // them, which is where they are read. A manual analysed before the move has them only in
  // this phase's own store, so it falls through to that rather than showing a step with no
  // tools and no text where both were read months ago.
  const detail = useMemo<Record<string, StepDetail>>(() => {
    const out: Record<string, StepDetail> = {};
    for (const s of steps) {
      out[s.category] = {
        images: sections[s.category],
        tools: alloc.tools[s.category] ?? st.tools[s.category],
        buildsOn: st.buildsOn[s.category],
        instructions: said.byStep[s.category] ?? st.instructions[s.category],
        reason: st.reasoning[s.category],
        took: st.timing[s.category],
      };
    }
    return out;
  }, [steps, sections, alloc.tools, said.byStep, st.tools, st.buildsOn, st.instructions,
    st.reasoning, st.timing]);

  // EVERY SUB-STEP THE MANUAL HAS, flattened in printed order — the list the navigator walks —
  // with what each holds and the pieces by name, made the way the joint view makes them (see
  // useSubStepTable).
  const { subPlaces, pieceName, made } = useSubStepTable(docId);

  // THE PLACES THE PAGE CAN STAND: the sub-step table over the whole manual, the same table cut
  // to one step ("1", "2"…), and the tree.
  //
  // NO PLACE PER SUB-STEP. There was one for every gesture — forty tiles — each a page with the
  // step's diagram, its printed text, the gesture's sentence and its chips. Every one of those
  // is in the table's row for it or on the Steps badge (the diagram is in its prompt, the
  // reasoning and the answer are the step's, one call per step), so the pages said again, one
  // at a time, what the table says at once.
  const places = useMemo(
    () => {
      type Place = {
        key: string;
        label: string;
        step: (typeof subPlaces)[number]['step'] | null;
        sub: (typeof subPlaces)[number]['sub'] | null;
        // ONE BLOCK, on one row: the tables and the tree are a handful of places, and a block
        // each put the tree alone on a second line.
        group: 'views';
      };
      const analysed = steps.filter(
        (s) => subPlaces.some((p) => p.step.category === s.category),
      );
      return [
        { key: ALL_SUBS, label: t('notice.steps.allSubs'), step: null, sub: null, group: 'views' },
        // A step nobody has analysed contributes no place: there is nothing to cut the
        // table to.
        ...analysed.map((s) => ({
          key: STEP_SUBS(s.category), label: String(s.num), step: s, sub: null, group: 'views' as const,
        })),
        // THE TREE LAST, and ONE of it: the whole manual as one object, or one step's pieces
        // each grown down through what they were built out of — which step is the tree's own
        // dropdown, not four places in this strip saying the same drawing four times.
        ...(analysed.length ? [
          { key: TREE_VIEW, label: t('notice.steps.tree'), step: null, sub: null, group: 'views' as const },
        ] : []),
      ] as Place[];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subPlaces, steps, language],
  );
  // WHICH RUN EACH SUB-STEP IS IN, over the whole manual — the same grouping the overview
  // draws its banners from, so the table and the gesture under it name one run the same way.
  // Held by KEY and not by index, so a re-analysis that gives a step one sub-step more does
  // not silently move the page to a different gesture. Falls back to the first place when
  // the key it was on is gone — which is what a fresh run does to every key at once.
  const [atKey, setAtKey] = useState('');
  const at = Math.max(0, places.findIndex((p) => p.key === atKey));
  const here = places[at];
  // WHICH STEP THE TREE IS TOPPED BY, from its dropdown — '' for the whole manual. Remembered
  // like the rest of where this browser was; a step no longer analysed falls back to the whole.
  const [treeAt, setTreeAt] = usePlace(docId, 'treeStep');
  const treeSteps = steps.filter((s) => subPlaces.some((p) => p.step.category === s.category));
  const treeStep = treeSteps.find((s) => s.category === treeAt) ?? null;
  // THE PLACES AS THE STRIP WANTS THEM. No mark: a place is not done or not done — it is the
  // tree, or a step's table, or one gesture — and the strip draws none where none is given.
  const segments = useMemo<SelectorPart[]>(
    () => places.map((p) => ({ key: p.key, name: p.label, group: p.group })),
    [places],
  );
  // AND THE ARROW KEYS, which is what forty gestures asks for whatever the control looks like.
  // They came free inside SubStepNav; the strip is shared with a phase that wires its own, so
  // they are wired here. See useArrowSteps for what it does about a typist and about an
  // overlay opened over the page — half of this phase is editable fields.
  const walk = useArrowSteps<HTMLDivElement>(
    at,
    places.length,
    (next) => places[next] && setAtKey(places[next].key),
  );


  // THE ONTOLOGY, SUBSCRIBED TO rather than read. Every part-kind question below goes through
  // the module cache, which answers "component" for everything until the fetch lands; holding a
  // subscription is what makes this page rebuild when it does.
  useCategories();

  // The whole manual on the overview, one step's rows when standing on a step. One table every
  // time — what changes is how much of it is passed in.
  const subTable = (
    <AllSubSteps
      pieces={pieceName}
      rows={here?.step
        ? subPlaces.filter((p) => p.step.category === here.step!.category)
        : subPlaces}
      // The whole manual, whichever slice is drawn: what a step has on the table includes what
      // the steps before it built, and only a walk over all of them knows that. Same pair as
      // the tree.
      all={subPlaces}
      items={items}
      made={made}
    />
  );

  // THE PARTS RECONCILIATION'S VERDICT, over the whole manual — and, given one, the table it
  // is about under it in the same card.
  const reconciliation = (under: ReactNode) => (
    <Reconciliation
      items={items}
      // Nothing to crop for: the crops are the table's column headers, and the table is not
      // drawn here. Rendering one per part off the PDF was several seconds of canvas work per
      // manual, spent on pictures this page never showed.
      itemCrops={[]}
      used={byStep}
      steps={steps}
      stepText={stepText}
      subSteps={st.subSteps}
      detail={detail}
      busy={busy}
      running={st.running}
      onZoom={setZoom}
      verdictOnly
    >
      {under}
    </Reconciliation>
  );

  // Show only the waiting control while a run-all hasn't reached this phase yet.
  if (pending) return <PendingPhase held={held} />;

  if (steps.length === 0) {
    return (
      <p className="text-center text-sm text-slate-500">
        {t('notice.cat2.noRun')}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {items.length === 0 && (
        <p className="text-center text-sm text-slate-400">
          {t('notice.steps.noParts').replace('{p}', t('notice.parts.phaseTitle'))}
        </p>
      )}
      {st.error && <p className="text-center text-sm text-rose-600">{st.error}</p>}
      {/* WHILE THE FIRST ANSWER IS ON ITS WAY: the table's card, empty, with the spinner in it —
          the page says where the sub-steps will land rather than showing nothing under the
          selector until the first step comes back. */}
      {subPlaces.length === 0 && busy && (
        <div className={`${SUB_TABLE_CARD} flex min-h-[12rem] items-center justify-center`}>
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      )}
      {/* AND THE SUB-STEP IN FRONT OF YOU. Nothing here while the manual has no analysis —
          the run button above is the whole of what there is to do then, and an empty
          navigator over an empty card is a page pretending to have an answer. */}
      {subPlaces.length > 0 && here && (
        <div className="flex flex-col items-center gap-4">
          {/* EVERY PLACE AT ONCE, and the one you are in lit. It was ‹ 1A › — two arrows
              and the name between them — which is a control for a walk and this is not a
              walk: a manual runs to forty gestures, and reaching 3F from the tree meant
              thirty presses past thirty things nobody wanted to look at.

              THE SAME STRIP THE PARTS PHASE DRAWS, open like it: every place on screen, in
              rows. It was one scrolling line, and most of the places were off its edge. So a
              place is one press away rather than thirty, and the shape of the manual — four
              tables, then the steps, then the gestures — is on screen instead of being
              something you learn by walking it.

              The arrow keys still walk it, and they are wired here now rather than inside the
              control: the old ‹ 1A › pager carried them. */}
          <div ref={walk} className="w-full">
            <PartSelector
              parts={segments}
              activeKey={here.key}
              onSelect={setAtKey}
              // One width for every tile, the widest label's — "All sub-steps".
              widthBy="group"
            />
          </div>
          <div className="w-full">
            {here.key === TREE_VIEW ? (
              <AssemblyTree
                rows={subPlaces}
                // The whole manual for the complete tree; one step's gestures for a step's.
                islands={treeStep
                  ? subPlaces.filter((p) => p.step.category === treeStep.category)
                  : subPlaces}
                made={made}
                items={items}
                // The file is called what the tree shows.
                name={treeStep
                  ? t('notice.steps.treeStep').replace('{n}', String(treeStep.num))
                  : t('notice.steps.treeAll')}
                corner={(
                  <CenteredSelect
                    look="pill"
                    value={treeStep?.category ?? ''}
                    options={[
                      { value: '', label: t('notice.steps.allSteps') },
                      ...treeSteps.map((s) => ({
                        value: s.category, label: `${t('notice.parts.stepRow')} ${s.num}`,
                      })),
                    ]}
                    onChange={setTreeAt}
                    ariaLabel={t('notice.steps.tree')}
                  />
                )}
              />
            ) : showRecon ? (
              // THE VERDICT OVER THE TABLE IT IS ABOUT, in one card — as Phase 4 draws its own.
              reconciliation(subTable)
            ) : subTable}
          </div>
        </div>
      )}

      {/* THE VERDICT, OVER THE WHOLE MANUAL, where there is no table for it to top: under a
          single gesture or the tree, it is still how you learn that some gesture somewhere
          disagrees with its step, named down to the step and the part. */}
      {showRecon && here?.key === TREE_VIEW && reconciliation(null)}

      <ImageLightbox src={zoom} alt="" onClose={() => setZoom(null)} />
    </div>
  );
}
